import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import JsonLd from '@/components/base/JsonLd';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://oopbuyspreadsheets.rest';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center px-4">
          <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
          <p className="text-foreground-500 mb-6">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-flex items-center h-10 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors">
            Back to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const articleUrl = `${SITE_URL}/${article.slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    url: articleUrl,
    datePublished: '2026-06-15',
    dateModified: '2026-06-22',
    author: {
      '@type': 'Organization',
      name: article.author,
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'OOPBuy Spreadsheet',
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    image: {
      '@type': 'ImageObject',
      url: article.imageUrl,
      width: '800',
      height: '500',
    },
    inLanguage: 'en',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'OOPBuy Spreadsheet Guides',
        item: `${SITE_URL}/`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: articleUrl,
      },
    ],
  };

  return (
    <>
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <nav className="sticky top-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="flex items-center justify-between h-[72px] px-4 md:px-6">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer group"
            >
              <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform"></i>
              <span className="text-sm font-medium hidden sm:inline">Back to OOPBuy Spreadsheet Home</span>
              <span className="text-sm font-medium sm:hidden">Back</span>
            </Link>
            <Link
              to="/"
              className="hidden md:flex items-center h-9 px-4 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
            >
              Home
            </Link>
            <Link
              to="/oopbuy-spreadsheet-guide"
              className="hidden md:flex items-center h-9 px-4 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors"
            >
              Shopping Guide
            </Link>
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center h-10 px-6 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            Buy It Now
          </a>
        </div>
      </nav>

      <article className="bg-background-50">
        <header className="bg-background-100 py-12 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-16 max-w-[800px] mx-auto">
            <nav aria-label="Breadcrumb" className="mb-5">
              <ol className="flex items-center gap-1.5 text-xs text-foreground-500">
                <li>
                  <Link to="/" className="hover:text-foreground-950 transition-colors">Home</Link>
                </li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                <li className="text-foreground-700 font-medium truncate max-w-[200px]">{article.title}</li>
              </ol>
            </nav>
            <div className="inline-flex items-center gap-2 h-8 px-4 rounded-full bg-primary-500 text-background-50 text-xs font-semibold mb-5">
              <i className="ri-article-line"></i>
              <span>SEO Guide</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl leading-tight font-bold text-foreground-950 mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-foreground-950 flex items-center justify-center">
                <span className="text-background-50 font-heading text-base font-bold">O</span>
              </div>
              <div>
                <span className="block text-sm font-semibold text-foreground-950">{article.author}</span>
                <span className="block text-xs text-foreground-500">Published June 2026 · 6 min read</span>
              </div>
            </div>
          </div>
        </header>

        <div className="w-full px-4 md:px-6 lg:px-16 max-w-[800px] mx-auto py-8 md:py-14">
          <div className="space-y-8">
            <p className="text-foreground-700 leading-relaxed text-base">{article.content.intro}</p>

            {article.content.sections.map((section, idx) => (
              <section key={idx}>
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-3">
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pi) => (
                  <p key={pi} className="text-foreground-700 leading-relaxed text-base mt-3">
                    {p}
                  </p>
                ))}
                {section.table && (
                  <div className="overflow-x-auto mt-4">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          {section.table.headers.map((h, hi) => (
                            <th key={hi} className="text-left py-3 px-4 font-semibold text-foreground-950">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-background-100">
                            {row.map((cell, ci) => (
                              <td key={ci} className={`py-3 px-4 ${ci === 0 ? 'text-foreground-700 font-medium' : 'text-foreground-500'}`}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {section.list && (
                  <ol className="list-decimal list-inside space-y-3 text-foreground-700 leading-relaxed text-base mt-3">
                    {section.list.map((item, li) => (
                      <li key={li}>{item}</li>
                    ))}
                  </ol>
                )}
              </section>
            ))}

            <section className="bg-background-100 rounded-2xl p-6 md:p-8 mt-8">
              <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                Ready to Start Using the OOPBuy Spreadsheet?
              </h3>
              <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                Browse all 14 product categories, set up your tracking sheets, and join thousands of resellers who have transformed their sourcing workflow.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  Go to OOPBuy Spreadsheet
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center h-12 px-8 rounded-full border border-foreground-300 text-foreground-800 font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-foreground-950 hover:text-background-50 transition-colors"
                >
                  Back to Homepage
                </Link>
              </div>
            </section>

            <section className="pt-8 border-t border-background-200 mt-8">
              <h4 className="font-sans text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                Related Guides
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/${a.slug}`}
                      className="flex items-center gap-3 p-3 rounded-xl border border-background-200 bg-background-50 hover:border-foreground-300 hover:bg-background-100 transition-all cursor-pointer group"
                    >
                      <div className="w-16 h-12 rounded-lg overflow-hidden shrink-0">
                        <img src={a.imageUrl} alt={a.title} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-xs text-foreground-700 font-medium line-clamp-2 group-hover:text-foreground-950 transition-colors">
                        {a.title}
                      </span>
                    </Link>
                  ))}
              </div>
            </section>

            <section className="pt-6 border-t border-background-200 mt-6">
              <h4 className="font-sans text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                Browse Categories
              </h4>
              <div className="flex flex-wrap gap-2">
                {categories.slice(0, 10).map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="h-9 px-4 rounded-full border border-background-200 bg-background-50 text-xs text-foreground-600 font-medium whitespace-nowrap cursor-pointer hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </section>
          </div>
        </div>
      </article>
    </>
  );
}